import type { HLDexName, HLMarketInfo } from "./types"

export const MAIN_DEX: HLDexName = ""
export const TRADE_XYZ_DEX: HLDexName = "xyz"
export const LAST_PERPS_MARKET_STORAGE_KEY = "defuse:last_perps_market"

const BUILDER_PERP_ASSET_ID_BASE = 100000
const BUILDER_PERP_ASSET_ID_STRIDE = 10000

const RWA_CATEGORIES = new Set([
  "commodities",
  "fx",
  "indices",
  "preipo",
  "stocks",
])

export type HLMarketAnnotation = {
  category?: string
  displayName?: string
  description?: string
  keywords?: string[]
}

export function deriveOrderAssetId(params: {
  dexIndex: number
  indexInDex: number
}): number {
  if (params.dexIndex === 0) return params.indexInDex
  return (
    BUILDER_PERP_ASSET_ID_BASE +
    params.dexIndex * BUILDER_PERP_ASSET_ID_STRIDE +
    params.indexInDex
  )
}

export function isBuilderDexOrderAssetId(orderAssetId: number): boolean {
  return orderAssetId >= BUILDER_PERP_ASSET_ID_BASE
}

export function getDexFromCoin(coin: string): HLDexName {
  return coin.toLowerCase().startsWith(`${TRADE_XYZ_DEX}:`)
    ? TRADE_XYZ_DEX
    : MAIN_DEX
}

export function getDexLabel(dex: HLDexName): string {
  return dex === TRADE_XYZ_DEX ? "trade.xyz" : "Hyperliquid"
}

export function getMarketSymbolFromName(coin: string): string {
  const separator = coin.indexOf(":")
  return separator === -1 ? coin : coin.slice(separator + 1)
}

export function isRwaMarket(params: {
  dex: HLDexName
  category?: string
}): boolean {
  if (params.dex !== TRADE_XYZ_DEX) return false
  if (!params.category) return true
  return RWA_CATEGORIES.has(params.category.toLowerCase())
}

export function toMarketRouteParam(coin: string): string {
  return encodeURIComponent(coin)
}

export function toMarketHref(coin: string): string {
  return `/perps/${toMarketRouteParam(coin)}`
}

export function decodeMarketRouteParam(param: string): string {
  try {
    return decodeURIComponent(param)
  } catch {
    return param
  }
}

export function resolveMarketFromRoute(
  param: string,
  markets: HLMarketInfo[]
): HLMarketInfo | null {
  const decoded = decodeMarketRouteParam(param)
  const exact = markets.find((market) => market.name === decoded)
  if (exact) return exact

  const dexSeparator = decoded.indexOf(":")
  if (dexSeparator !== -1) {
    const dex = decoded.slice(0, dexSeparator).toLowerCase()
    const symbol = decoded.slice(dexSeparator + 1).toUpperCase()
    return (
      markets.find(
        (market) =>
          market.dex.toLowerCase() === dex &&
          getMarketSymbolFromName(market.name).toUpperCase() === symbol
      ) ?? null
    )
  }

  const upper = decoded.toUpperCase()
  return (
    markets.find(
      (market) => market.dex === MAIN_DEX && market.name.toUpperCase() === upper
    ) ?? null
  )
}

export function normalizeMarketDisplay(params: {
  coin: string
  annotation?: HLMarketAnnotation
  fallbackName?: string
}): { displaySymbol: string; displayName: string; keywords: string[] } {
  const symbol = getMarketSymbolFromName(params.coin)
  const displaySymbol = params.annotation?.displayName ?? symbol
  const displayName =
    params.annotation?.displayName ?? params.fallbackName ?? symbol

  return {
    displaySymbol,
    displayName,
    keywords: params.annotation?.keywords ?? [],
  }
}
